<script setup>
import { computed, ref, watch } from 'vue';
import _ from '@/lib/lodash';

const props = defineProps({
  label: {
    type: String,
    default() {
      return '';
    },
  },
  insertLabel: {
    type: Boolean,
    default() {
      return false;
    },
  },
  list: {
    type: Array,
    default() {
      return [];
    },
  },
  isSearch: {
    type: Boolean,
    default() {
      return false;
    },
  },
  type: {
    type: String,
    default() {
      return 'default';
    },
  },
  modelValue: {
    type: [String, Number, Array],
    default() {
      return '';
    },
  },
  optionNameParam: {
    type: String,
    default() {
      return 'text';
    },
  },
  optionLabelParam: {
    type: String,
    default() {
      return 'text';
    },
  },
  optionValueParam: {
    type: String,
    default() {
      return 'id';
    },
  },
  placeholder: {
    type: [String, Number],
    default() {
      return '';
    },
  },
  disabled: {
    type: Boolean,
    default() {
      return false;
    },
  },
  emptyValues: {
    type: [Array],
    default() {
      return ['', null];
    },
  },
  valueOnClear: {
    type: [String, Number, Boolean, Function],
    default() {
      return '';
    },
  },
  popperClass: {
    type: String,
    default() {
      return '';
    },
  },
  prependIcon: {
    type: String,
    default() {
      return '';
    },
  },
  prependText: {
    type: String,
    default() {
      return '';
    },
  },
  required: {
    type: Boolean,
    default() {
      return false;
    },
  },
  fitInputWidth: {
    type: Boolean,
    default() {
      return true;
    },
  },
  multiple: {
    type: Boolean,
    default() {
      return false;
    },
  },
  teleported: {
    type: Boolean,
    default() {
      return true;
    },
  },
  width: {
    type: [String, Number],
    default() {
      return '';
    },
  },
  isError: {
    type: Boolean,
    default() {
      return false;
    },
  },
  error: {
    type: [String, Number, Array],
    default() {
      return '';
    },
  },
  errorClearOnChange: {
    type: Boolean,
    default: true,
  },
  isSort: {
    type: Boolean,
    default: false,
  },
  remoteSearch: {
    type: Function,
    default: null,
  },
  mobileShiftFix: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'change', 'update:error']);

//#region data
const isUp = ref(false);
const isDown = ref(true);
const selectIsOpen = ref(false);
const searchValue = ref('');
const uiSelect = ref(null);
const remoteSearchValue = ref([]);
//#endregion

//#region computed
const insertLabelText = computed(() => {
  return props.label;
});
const pPopperClass = computed(() => {
  const classes = ['ui-select__dropdown', props.popperClass];
  if (props.mobileShiftFix) {
    classes.unshift('ui-select__dropdown_moblie-shift-fix');
  }

  return classes.join(' ');
});
const pWidth = computed(() => {
  return props.width === '' ? '100%' : `${props.width}px`;
});
const currentError = computed(() => {
  return _.isArray(props.error) ? props.error[0].message : props.error;
});
const pList = computed(() => {
  const searchValueLocal = searchValue.value.toLowerCase().trim();
  if (searchValueLocal === '') {
    return props.list;
  } else {
    if(props.remoteSearch) {
      return remoteSearchValue.value;
    } else {
      return props.list.filter((item) => {
        const name = item.text?.toLowerCase();
        return name.includes(searchValueLocal);
      });
    }
  }
});
//#endregion

//#region methods
const debouncedSearch = _.debounce(async (newValue, oldValue) => {
  if (newValue !== oldValue && props.isSearch && props.remoteSearch) {
    remoteSearchValue.value = await props.remoteSearch(newValue);
  }
}, 500);

const handleOptionTouch = (event, value) => {
  if (props.multiple && ('ontouchstart' in window || navigator.maxTouchPoints > 0)) {
    event.preventDefault();

    const currentValues = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
    const index = currentValues.indexOf(value);

    if (index > -1) {
      currentValues.splice(index, 1);
    } else {
      currentValues.push(value);
    }

    emit('update:modelValue', currentValues);
    emit('change', currentValues);
  }
};

const handleVisibleChange = async (value) => {
  emit('update:error', null);
  setTimeout(() => {
    const dropdown = uiSelect.value?.tooltipRef?.contentRef?.contentRef?.popperContentRef;
    const placement = dropdown.dataset.popperPlacement;
    isUp.value = placement === 'top-start';
    isDown.value = placement !== 'top-start';
  }, 0); //для того что бы получить актуальное положение
  selectIsOpen.value = value;
  setTimeout(() => {
    if (!value) {
      searchValue.value = '';
    }
  }, 100); //для того что бы при закрытии dropdown не скакал
};
const handleChange = async (value) => {
  const data = props.remoteSearch && pList.value.find((item) => item[props.optionValueParam] === value);

  emit('change', value, data);
  if (props.errorClearOnChange) {
    emit('update:error', null);
  }
};
//#endregion

watch(searchValue, debouncedSearch);
</script>
<template>
  <div
    :class="[
      'ui-select',
      `ui-select_${type}`,
      { 'ui-select_disabled': disabled },
      { 'ui-select_error': currentError || isError },
      { 'ui-select_is-sort': isSort },
    ]"
    :style="{ width: pWidth }"
  >
    <UiLabel
      v-if="label && !insertLabel"
      class="ui-select__label"
      :label="label"
      :required="required"
      :type="type"
    />
    <ElSelect
      ref="uiSelect"
      :class="[
        'ui-select__element',
        {'ui-select__element_dropped-top': selectIsOpen && isUp},
        {'ui-select__element_dropped-down': selectIsOpen && isDown}
      ]"
      :disabled="disabled"
      :empty-values="emptyValues"
      :fallback-placements="['bottom-start', 'top-start']"
      :fit-input-width="fitInputWidth"
      :model-value="modelValue"
      :multiple="multiple"
      :offset="0"
      :placeholder="placeholder"
      :popper-class="pPopperClass"
      :show-arrow="false"
      :teleported="teleported"
      :value-on-clear="valueOnClear"
      collapse-tags
      placement="bottom-start"
      @change="handleChange"
      @update:model-value="emit('update:modelValue', $event)"
      @visible-change="handleVisibleChange"
    >
      <template
        v-if="prependIcon !== '' || prependText"
        v-slot:prefix
      >
        <span
          v-if="prependText"
          class="ui-select__prepend-text"
        >{{ prependText }}</span>
        <UiIcon
          v-else
          class="ui-select__prepend-icon"
          :name="prependIcon"
          :size="14"
          color="#303634"
        />
      </template>
      <template
        v-slot:header
      >
        <slot name="header">
          <input
            v-if="isSearch"
            v-model="searchValue"
            :placeholder="$t('promoCodes.promocodes.search')"
            autocomplete="off"
            class="ui-select__element-search"
            type="text"
          >
        </slot>
      </template>
      <template v-slot:empty>
        <div class="ui-select__no-data">
          {{ $t('ui.noData') }}
        </div>
      </template>
      <template
        v-if="insertLabel"
        v-slot:label="{ label }"
      >
        <span class="ui-select__label-insert">{{ insertLabelText }}: </span>
        <span>{{ label }}</span>
      </template>
      <slot name="options">
        <ElOption
          v-for="item in pList"
          :key="item[optionValueParam]"
          :label="item[optionLabelParam]"
          :value="item[optionValueParam]"
          @touchstart="handleOptionTouch($event, item[optionValueParam])"
        >
          <span>{{ item[optionNameParam] }}</span>
        </ElOption>
      </slot>
    </ElSelect>
    <UiHelp
      v-if="currentError"
      :is-error="true"
      :text="currentError"
      class="mt-2xs"
    />
  </div>
</template>

<style lang="scss">
.ui-select {
  display: flex;
  flex-direction: column;
  font-family: Open Sans, serif;
  width: 100%;

  &__prepend-icon {
    margin-left: -1px;
    margin-top: -2px;
    margin-right: 1px;
  }

  &__prepend-text {
    font-weight: 600;
    font-size: 13px;
    color: #000;
  }

  .el-select__wrapper {
    padding-right: 9px;
  }

  .el-select__caret {
    // width: 6px;
    // height: 6px;

    // &:before {
    //   margin-top: -3px;
    //   display: block;
    //   content: '';
    //   width: 100%;
    //   height: 100%;
    //   border: 2px solid #7d7d7d;
    //   border-left: none;
    //   border-top: none;
    //   transform: rotate(45deg);
    // }

    &:before {
      content: '\f107';
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: normal;
      text-align: center;
      font-family: 'Font Awesome 6 Pro';
      font-size: 13px;
      display: var(--fa-display, inline-block);
      font-style: normal;
      font-variant: normal;
      line-height: 1;
      text-rendering: auto;
      color: #7d7d7d;
    }

    svg {
      display: none;
    }
  }

  &__element {
    height: 100%;

    .el-select__wrapper {
      display: inline-flex;
      align-items: center;
      position: relative;
      width: 100%;
      color: #303634;
      line-height: normal;
      font-size: 12px;
      box-shadow: none;
      min-height: auto;
      height: 100%;
      padding: 8px 16px;

      &.is-hovering:not(.is-focused) {
        box-shadow: none;
      }
    }

    &_dropped-down {
      .el-select__wrapper {
        border-radius: 8px 8px 0 0;
      }
    }

    &_dropped-top {
      .el-select__wrapper {
        border-radius: 0 0 8px 8px;
      }
    }

    .el-select__input,
    .el-select__selected-item {
      color: #000;
      font-family: Open Sans, serif;
      font-size: 12px;
      font-weight: 400;
      line-height: 28px;
    }
  }

  &_default & {
    &__element {
      .el-select__wrapper {
        border: 1px solid #d3d3d3;
        background-image: linear-gradient(to top, rgba(0, 0, 0, 0.06) 0%, rgba(255, 255, 255, 0.06) 100%);
        border-radius: 8px;
        height: 34px;
      }

      &_dropped-down {
        .el-select__wrapper {
          border-radius: 8px 8px 0 0;
        }
      }

      &_dropped-top {
        .el-select__wrapper {
          border-radius: 0 0 8px 8px;
        }
      }
    }
  }

  &_registration & {
    &__element {
      .el-select__input,
      .el-select__selected-item {
        font-size: 14px;
      }
      .el-select__wrapper {
        background: transparent;
        border-bottom: 2px solid #ccc;
        border-radius: 0;
        padding: 0;
        height: 45px;
      }

      &_dropped-down,
      &_dropped-top {
        .el-select__wrapper {
          border-color: var(--color-bg-primary-hover);
        }
      }
    }
  }

  &_multiselect {
    .ui-select__element {
      .el-select__wrapper {
        border: 1px solid #d3d3d3;
        background-image: linear-gradient(to top, rgba(0, 0, 0, 0.06) 0%, rgba(255, 255, 255, 0.06) 100%);
        border-radius: 8px;
        height: 34px;
      }

      .el-select__selection {
        flex-wrap: nowrap;
      }

      &_dropped-down {
        .el-select__wrapper {
          border-radius: 8px 8px 0 0;
        }
      }

      &_dropped-top {
        .el-select__wrapper {
          border-radius: 0 0 8px 8px;
        }
      }
    }

    &.ui-select {
      .el-tag {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 10;
        height: 24px;
        max-width: 144px !important;
        padding-left: 8px;
        background-color: #efefef;
        border-radius: 3px;
        box-sizing: border-box;
        border: none;
        cursor: pointer;
        text-transform: none;
        font-size: 12px;
        margin-right: 0;
        margin-bottom: 0;

        .el-tag__close {
          color: var(--site-text-color);
          display: flex;
          align-self: stretch;
          height: 24px;
          width: 24px;
          margin: -4px -8px -4px 2px;
          border-radius: 3px;

          &:hover {
            border-radius: 3px;
            background-color: var(--red-color);
          }
        }
      }

      .el-select__tags-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 12px;
      }
    }

    &.ui-select_multiselect {
      .el-select__selected-item:first-child {
        overflow: hidden;
        .el-tag {
          overflow: hidden;
        }
      }
    }

  }
  &_filter {
    position: relative;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    height: 42px;
    background: #fff;
    border-top: 1px solid #e7ecf1;
    cursor: pointer;

    .ui-select__label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: calc(50% - 18px);
    }

    .el-select__selection {
      max-width: calc(50% - 18px);
      justify-content: right;
    }

    .el-select__wrapper {
      background: transparent;
      justify-content: flex-end;
    }

    .el-select__input,
    .el-select__selected-item {
      font-family: Open Sans, serif;
      font-weight: 400;
      text-align: right;
      padding: 0;
      line-height: 40px;
      font-size: 12px;
      color: #303634;
    }
    .ui-select__element {
      width: 100%;
    }

    .el-select-dropdown.is-multiple .el-select-dropdown__item.is-selected::after {
      background-color: #303634;
    }

    &.ui-select {
      .el-tag {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 10;
        height: 24px;
        max-width: 144px !important;
        padding-left: 8px;
        background-color: #efefef;
        border-radius: 3px;
        box-sizing: border-box;
        border: none;
        cursor: pointer;
        text-transform: none;
        font-size: 12px;
        margin-right: 0;
        margin-bottom: 0;

        .el-tag__close {
          color: var(--site-text-color);
          display: flex;
          align-self: stretch;
          height: 24px;
          width: 24px;
          margin: -4px -8px -4px 2px;
          border-radius: 3px;

          &:hover {
            border-radius: 3px;
            background-color: var(--red-color);
          }
        }
      }

      .el-select__tags-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 12px;
      }
    }
  }

  &_error & {
    &__element {
      .el-select__wrapper {
        border-color: #ff5c70;
      }
    }
  }

  &_disabled {
    // cursor: not-allowed;
    // opacity: .5;

    .ui-select__label,
    .el-select__caret {
      opacity: .5;
    }

    .el-select__selected-item {
      color: #303634;
    }

    .el-select__wrapper.is-disabled {
      background-color: unset;
    }
  }

  &_is-sort & {
    &__element {
      .el-select__wrapper {
        border-radius: 8px 0 0 8px;
      }
      &_dropped-down {
        .el-select__wrapper {
          border-radius: 8px 0 0 0;
        }
      }
      &_dropped-top {
        .el-select__wrapper {
          border-radius: 0 0 0 8px;
        }
      }
    }
  }

  &__label-insert{
    font-weight: 600;
    font-size: 13px;
  }
  &__element-search {
    display: flex;
    flex-direction: column;
    position: relative;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    padding: 4px 12px;
    color: #2c2c2c;
    font-size: 14px;
    outline: none;
    background: transparent;
    width: 100%;
    /* Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &__dropdown {
    background-color: #fff;
    border: 1px solid #d3d3d3;
    border-top: none;
    box-sizing: border-box;
    border-radius: 0 0 8px 8px;
    font-family: Open Sans, serif;

    .el-select-dropdown {
      &__wrap {
        max-height: 200px;
      }

      &__empty {
        padding: 0;
      }
    }

    &.el-select__popper.el-popper {
      border: none;
      box-shadow: none;
    }

    .el-select-dropdown__header {
      padding: 4px;
      border-bottom: none;
    }

    .el-select-dropdown {
      &__item {
        display: flex;
        align-items: center;
        font-size: 14px;
        font-weight: 400;
        font-stretch: normal;
        font-style: normal;
        line-height: 21px;
        padding: 6px;
        color: #3c3c3c;
        height: auto;
        white-space: break-spaces;

        &.is-hovering,
        &:hover {
          background-color: var(--color-bg-select-hover);
        }

        &.is-selected {
          background-color: var(--color-bg-select-hover);
          color: #3c3c3c;
        }
      }
    }
  }

  &__no-data {
    padding: 6px;
    margin: 0;
    text-align: left;
    font-weight: 400;
    color: #3c3c3c;
    font-size: 14px;
  }
}

[data-popper-placement="top-start"] {
  .ui-select__dropdown {
    border-radius: 8px 8px 0 0;
    border-top: 1px solid #d3d3d3;
    border-bottom: none;
  }
}

@media (max-width: 650px) {
  .ui-select__dropdown {
    .el-select-dropdown__item:not(.is-selected):not(.is-active) {
      background-color: transparent !important;
    }
  }
}
</style>
